import RytmWebflow from 'rytm-webflow'
import { gsap } from 'gsap'

const MARQUEE_SPEED = 80 // px per second
const RESIZE_DEBOUNCE = 150

// rytm-webflow's AswapEvent isn't exported; recreate the shape so super.* calls typecheck.
interface AswapEvent extends Event {
  target: (EventTarget & { freshContent: Element }) | null
}

class AlertsController extends RytmWebflow.Controller {
  private _container: HTMLElement | null = null
  private _viewport: HTMLElement | null = null
  private _track: HTMLElement | null = null
  private _toggle: HTMLButtonElement | null = null
  private _iconPause: HTMLElement | null = null
  private _iconPlay: HTMLElement | null = null
  private _originalHTML = ''
  private _marquee: gsap.core.Tween | null = null
  private _revealTl: gsap.core.Timeline | null = null
  private _onToggleClick: (() => void) | null = null
  private _onResize: (() => void) | null = null
  private _resizeTimer: number | null = null
  private _paused = false
  private _revealed = false

  addEventListeners() {
    super.addEventListeners()

    const container = document.querySelector(this.getContainerSelector()) as HTMLElement | null
    if (!container) return

    this._container = container
    this._viewport = container.querySelector('.home-alerts__viewport') as HTMLElement | null
    this._track = container.querySelector('[data-alerts-track]') as HTMLElement | null
    this._toggle = container.querySelector('[data-alerts-toggle]') as HTMLButtonElement | null
    this._iconPause = container.querySelector('[data-alerts-icon-pause]') as HTMLElement | null
    this._iconPlay = container.querySelector('[data-alerts-icon-play]') as HTMLElement | null

    if (!this._viewport || !this._track || !this._toggle) return

    this._originalHTML = this._track.innerHTML
    this._paused = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    this._build()
    this._playReveal()
    this._bindEvents()

    // Re-measure once fonts have settled so the loop stays seamless after a font swap.
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        if (this._container) this._build()
      })
    }
  }

  removeEventListeners() {
    super.removeEventListeners()
    if (this._onToggleClick && this._toggle) {
      this._toggle.removeEventListener('click', this._onToggleClick)
    }
    if (this._onResize) {
      window.removeEventListener('resize', this._onResize)
    }
    if (this._resizeTimer !== null) {
      window.clearTimeout(this._resizeTimer)
      this._resizeTimer = null
    }
    this._onToggleClick = null
    this._onResize = null
  }

  onViewHidden(e: AswapEvent) {
    super.onViewHidden(e)
    this._marquee?.kill()
    this._marquee = null
    this._revealTl?.kill()
    this._revealTl = null
    this._container = null
    this._viewport = null
    this._track = null
    this._toggle = null
    this._iconPause = null
    this._iconPlay = null
    this._originalHTML = ''
    this._revealed = false
  }

  private _bindEvents() {
    this._onToggleClick = () => this._togglePause()
    this._toggle!.addEventListener('click', this._onToggleClick)
    this._onResize = () => {
      if (this._resizeTimer !== null) window.clearTimeout(this._resizeTimer)
      this._resizeTimer = window.setTimeout(() => {
        this._resizeTimer = null
        this._build()
      }, RESIZE_DEBOUNCE)
    }
    window.addEventListener('resize', this._onResize)
  }

  private _build() {
    if (!this._track || !this._viewport) return

    this._marquee?.kill()
    this._marquee = null

    this._track.innerHTML = this._originalHTML
    gsap.set(this._track, { x: 0 })

    const viewportWidth = this._viewport.clientWidth
    if (viewportWidth <= 0) return

    const originalItemCount = this._track.querySelectorAll('.home-alerts__item').length
    const originalWidth = this._track.scrollWidth
    if (originalWidth <= 0) return

    // Duplicate until the track holds at least one full authored sequence past the viewport.
    // Then looping by exactly `originalWidth` shifts content one identical period — seamless.
    let safety = 32
    while (this._track.scrollWidth < viewportWidth + originalWidth && safety-- > 0) {
      this._track.innerHTML += this._originalHTML
    }

    // Keep only the authored alerts in the accessibility tree; duplicates are decorative.
    this._track.querySelectorAll('.home-alerts__item').forEach((el, idx) => {
      if (idx >= originalItemCount) el.setAttribute('aria-hidden', 'true')
    })

    this._marquee = gsap.to(this._track, {
      x: -originalWidth,
      duration: originalWidth / MARQUEE_SPEED,
      ease: 'none',
      repeat: -1,
      paused: true,
    })

    if (!this._paused && this._revealed) this._marquee.resume()
    this._setToggleState(this._paused)
  }

  private _playReveal() {
    if (!this._container || !this._track || !this._toggle) return

    this._revealTl?.kill()

    const items = this._track.querySelectorAll('.home-alerts__item')
    const tl = gsap.timeline()
    this._revealTl = tl

    tl.fromTo(this._container, { opacity: 0 }, { opacity: 1, duration: 0.25, ease: 'none' }, 0)
    if (items.length) {
      tl.fromTo(items,
        { yPercent: -100 },
        { yPercent: 0, duration: 0.6, ease: 'power3.out', stagger: { amount: 0.35, from: 'start' }, clearProps: 'transform' },
        0.1)
    }
    tl.fromTo(this._toggle,
      { xPercent: 100 },
      { xPercent: 0, duration: 0.6, ease: 'power3.out', clearProps: 'transform' },
      0.1)

    tl.eventCallback('onComplete', () => {
      this._revealed = true
      if (this._marquee && !this._paused) this._marquee.resume()
    })
  }

  private _togglePause() {
    if (!this._marquee) return
    this._paused = !this._paused
    if (this._paused) {
      this._marquee.pause()
    } else {
      this._marquee.resume()
    }
    this._setToggleState(this._paused)
  }

  private _setToggleState(paused: boolean) {
    if (!this._toggle || !this._iconPause || !this._iconPlay) return
    const label = paused ? this._toggle.dataset.labelPlay : this._toggle.dataset.labelPause
    if (label) this._toggle.setAttribute('aria-label', label)
    this._toggle.setAttribute('aria-pressed', paused ? 'true' : 'false')
    this._iconPause.classList.toggle('hidden', paused)
    this._iconPlay.classList.toggle('hidden', !paused)
  }
}

export default AlertsController
